// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Hand-rolled SVG primitives shared by every chart. No charting library, on
// purpose: the encodings this site needs (a broken waterfall joint, an
// outline-only tier-3 mark, a line that BREAKS at a missing year) are exactly
// the things chart libraries make hard.

export const SVG_NS = 'http://www.w3.org/2000/svg';

export function el<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string | number | null | undefined> = {},
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null) continue;
    node.setAttribute(k, String(v));
  }
  return node;
}

/**
 * A chart root.
 *
 * `role="group"`, never `role="img"`. `role="img"` makes the SVG an
 * accessibility LEAF, which prunes every focusable mark inside it from the
 * tree while leaving those marks in the tab order — a screen reader user tabs
 * into things that announce nothing. That shipped on a sibling site.
 */
export function chartSvg(width: number, height: number, label: string): SVGSVGElement {
  const svg = el('svg', {
    viewBox: `0 0 ${width} ${height}`,
    class: 'chart',
    preserveAspectRatio: 'xMidYMid meet',
    role: 'group',
    'aria-label': label,
  });
  return svg;
}

/** Guard every coordinate: one NaN silently voids an entire path element. */
export function finite(n: number | null | undefined, fallback = 0): number {
  return n != null && Number.isFinite(n) ? n : fallback;
}

export interface Scale {
  (v: number): number;
  domain: [number, number];
  range: [number, number];
  invert(px: number): number;
  ticks(count?: number): number[];
}

export function linearScale(domain: [number, number], range: [number, number]): Scale {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0 || 1;
  const fn = ((v: number) => r0 + ((v - d0) / span) * (r1 - r0)) as Scale;
  fn.domain = domain;
  fn.range = range;
  fn.invert = (px: number) => d0 + ((px - r0) / (r1 - r0 || 1)) * span;
  fn.ticks = (count = 6) => niceTicks(d0, d1, count);
  return fn;
}

/**
 * A log scale, for the two views where the quantity spans orders of magnitude.
 *
 * Refuses non-positive input rather than clamping it: a log axis that silently
 * maps 0 and negatives onto its floor invents data at the bottom of the chart.
 * Callers must filter first, and the site's log views only carry strictly
 * positive quantities (land value, receipts) for that reason.
 */
export function logScale(domain: [number, number], range: [number, number]): Scale {
  const d0 = Math.max(domain[0], 1e-9);
  const d1 = Math.max(domain[1], d0 * 10);
  const l0 = Math.log10(d0); const l1 = Math.log10(d1);
  const [r0, r1] = range;
  const fn = ((v: number) => {
    if (!(v > 0)) return NaN;
    return r0 + ((Math.log10(v) - l0) / (l1 - l0 || 1)) * (r1 - r0);
  }) as Scale;
  fn.domain = [d0, d1];
  fn.range = range;
  fn.invert = (px: number) => 10 ** (l0 + ((px - r0) / (r1 - r0 || 1)) * (l1 - l0));
  fn.ticks = () => {
    const out: number[] = [];
    for (let e = Math.floor(l0); e <= Math.ceil(l1); e++) {
      for (const m of [1, 2, 5]) {
        const v = m * 10 ** e;
        if (v >= d0 && v <= d1) out.push(v);
      }
    }
    return out;
  };
  return fn;
}

export function niceTicks(min: number, max: number, count = 6): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return [min];
  const span = max - min;
  const raw = span / Math.max(1, count);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm >= 7.5 ? 10 : norm >= 3.5 ? 5 : norm >= 1.5 ? 2 : 1) * mag;
  const start = Math.ceil(min / step) * step;
  const out: number[] = [];
  for (let v = start; v <= max + step * 1e-6; v += step) out.push(Math.abs(v) < step * 1e-9 ? 0 : v);
  return out;
}

export function extent(values: (number | null | undefined)[]): [number, number] {
  let lo = Infinity; let hi = -Infinity;
  for (const v of values) {
    if (v == null || !Number.isFinite(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (!Number.isFinite(lo)) return [0, 1];
  return [lo, hi];
}

/**
 * A polyline that BREAKS at every gap rather than bridging it.
 *
 * Returns an array of path `d` strings, one per unbroken run. A single path
 * with the gaps skipped would draw a straight line across a missing decade and
 * read as a smooth trend — the quietest lie available in a time series.
 */
export function brokenPath(
  points: Array<{ x: number; y: number | null }>,
): string[] {
  const runs: string[] = [];
  let cur: string[] = [];
  for (const p of points) {
    if (p.y == null || !Number.isFinite(p.y) || !Number.isFinite(p.x)) {
      if (cur.length > 1) runs.push(cur.join(' '));
      cur = [];
      continue;
    }
    cur.push(`${cur.length ? 'L' : 'M'}${p.x.toFixed(2)},${p.y.toFixed(2)}`);
  }
  if (cur.length > 1) runs.push(cur.join(' '));
  // A single isolated point would otherwise vanish — emit a degenerate segment
  // so one surviving year in a run of gaps is still visible.
  return runs;
}

/** Points that survive as ISOLATED values, so they can be drawn as dots. */
export function isolatedPoints(
  points: Array<{ x: number; y: number | null }>,
): Array<{ x: number; y: number }> {
  const out: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (p.y == null || !Number.isFinite(p.y)) continue;
    const prev = points[i - 1];
    const next = points[i + 1];
    const prevOk = prev && prev.y != null && Number.isFinite(prev.y);
    const nextOk = next && next.y != null && Number.isFinite(next.y);
    if (!prevOk && !nextOk) out.push({ x: p.x, y: p.y });
  }
  return out;
}

/** The 45-degree hatch used for tier-4 marks and the provisional year. */
export function hatchDefs(id: string, colour: string, opacity = 0.9): SVGDefsElement {
  const defs = el('defs');
  const pattern = el('pattern', {
    id, width: 6, height: 6, patternUnits: 'userSpaceOnUse', patternTransform: 'rotate(45)',
  });
  pattern.append(el('rect', { width: 6, height: 6, fill: 'transparent' }));
  pattern.append(el('line', { x1: 0, y1: 0, x2: 0, y2: 6, stroke: colour, 'stroke-width': 2, opacity }));
  defs.append(pattern);
  return defs;
}

/** Attach a styled hover tooltip AND an accessible name to a mark. */
export function markTip(node: SVGElement, lines: (string | null | undefined | false)[]): void {
  const text = lines.filter(Boolean).join('\n');
  // setAttribute takes RAW newlines — `white-space: pre-line` on .hover-tip
  // renders them. `&#10;` here would print those literal characters.
  node.setAttribute('data-tip', text);
  // aria-label, never a <title> child: a native <title> is an unstyled, slow,
  // touch-invisible second tooltip that fights the real one.
  node.setAttribute('aria-label', text.replace(/\n/g, '. '));
}

export function axisBottom(
  svg: SVGElement, scale: Scale, y: number,
  opts: { format?: (v: number) => string; ticks?: number[]; title?: string; x0?: number; x1?: number } = {},
): void {
  const g = el('g', { class: 'axis' });
  const [r0, r1] = scale.range;
  g.append(el('line', { class: 'axis-line', x1: opts.x0 ?? r0, y1: y, x2: opts.x1 ?? r1, y2: y }));
  const ticks = opts.ticks ?? scale.ticks();
  for (const t of ticks) {
    const x = scale(t);
    if (!Number.isFinite(x)) continue;
    const label = el('text', { class: 'axis-text', x, y: y + 15, 'text-anchor': 'middle' });
    label.textContent = opts.format ? opts.format(t) : String(t);
    g.append(label);
  }
  if (opts.title) {
    const t = el('text', { class: 'axis-title', x: (r0 + r1) / 2, y: y + 32, 'text-anchor': 'middle' });
    t.textContent = opts.title;
    g.append(t);
  }
  svg.append(g);
}

export function axisLeft(
  svg: SVGElement, scale: Scale, x: number,
  opts: { format?: (v: number) => string; ticks?: number[]; title?: string; gridTo?: number } = {},
): void {
  const g = el('g', { class: 'axis' });
  const ticks = opts.ticks ?? scale.ticks();
  for (const t of ticks) {
    const y = scale(t);
    if (!Number.isFinite(y)) continue;
    if (opts.gridTo != null) {
      g.append(el('line', { class: t === 0 ? 'zero-line' : 'grid-line', x1: x, y1: y, x2: opts.gridTo, y2: y }));
    }
    const label = el('text', { class: 'axis-text', x: x - 8, y: y + 4, 'text-anchor': 'end' });
    label.textContent = opts.format ? opts.format(t) : String(t);
    g.append(label);
  }
  if (opts.title) {
    const [r0, r1] = scale.range;
    const t = el('text', {
      class: 'axis-title', x: 0, y: 0, 'text-anchor': 'middle',
      transform: `translate(${x - 46},${(r0 + r1) / 2}) rotate(-90)`,
    });
    t.textContent = opts.title;
    g.append(t);
  }
  svg.append(g);
}

/**
 * Wrap a chart in a horizontal scroller.
 *
 * Wide-by-nature content must scroll inside its own box rather than stretching
 * the page — a page-level sideways scroll at 375px is a hard fail.
 */
export function chartFrame(minWidth?: number): { frame: HTMLDivElement; scroll: HTMLDivElement } {
  const frame = document.createElement('div');
  frame.className = 'chart-frame';
  const scroll = document.createElement('div');
  scroll.className = 'chart-scroll';
  if (minWidth) scroll.style.minWidth = '0';
  frame.append(scroll);
  return { frame, scroll };
}
